import { Link } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Card,
  DataTable,
  PageHeader,
  Spinner,
} from "@empite/ui-components";
import type { TenantRosterRow } from "../admin-client";
import { useTenantRoster } from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { StatusBadge, when } from "./common";
import { intervalLabel, tokenCount } from "./tiers";

/**
 * Companies — every operator tenant, the tier it is on, and how much of that
 * tier is gone.
 *
 * It is the ONE screen in this console that crosses the tenant boundary on
 * purpose, and `tenants:read_all` is what says so. That permission is in the
 * Admin API's platform-only set, so AllowedInScope refuses it to a
 * tenant-confined subject whatever roles they hold — every other screen here
 * reaches exactly one company because its store put the boundary in the WHERE
 * clause, and this one reaches all of them because the question it answers
 * ("who is about to run out") only exists across them.
 *
 * A DIFFERENT GRANT FROM THE TIER EDITOR next door, deliberately: seeing that a
 * company has spent its allowance is support work, and changing what that
 * allowance costs is not.
 *
 * WHY A TENANT CAN SHOW NO TIER AT ALL. The platform's own tenant is in exactly
 * that state, and so is any company between signing up and being subscribed.
 * The row says "not metered" rather than drawing an allowance of zero, because
 * an unmetered tenant is not a blocked one — inventing a limit here would make
 * this screen disagree with the gateway about who is refused.
 */
export function Tenants() {
  return (
    <RequireOperator>
      {(session) =>
        can(session.operator, "tenants:read_all") ? (
          <TenantRoster />
        ) : (
          <>
            <PageHeader title="Companies" />
            <Card>
              <Alert tone="info" title="This list crosses the tenant boundary">
                <p>
                  It is every operator company on the platform, so only platform
                  staff can read it. Your own company&rsquo;s tier and how much of
                  it is left are on <strong>Billing</strong>.
                </p>
              </Alert>
            </Card>
          </>
        )
      }
    </RequireOperator>
  );
}

/** How full a tenant's allowance is. Committed — settled usage plus what is
 *  still in the air — is the figure the hard block compares, so it is the
 *  figure the bar is drawn from; using tokens_used alone would show a company
 *  as having room at the moment the gateway starts refusing it. */
function consumption(row: TenantRosterRow): { percent: number; label: string } | null {
  if (!row.usage) {
    return null;
  }
  const { committed_tokens, tokens_allowance } = row.usage;
  const percent =
    tokens_allowance === 0 ? 100 : Math.min(100, Math.round((committed_tokens / tokens_allowance) * 100));
  return {
    percent,
    label: `${tokenCount(committed_tokens)} / ${tokenCount(tokens_allowance)}`,
  };
}

function TenantRoster() {
  const roster = useTenantRoster();

  if (roster.isPending) {
    return <Spinner label="Loading companies" />;
  }
  if (roster.error) {
    return <ErrorAlert error={roster.error} title="Could not load the companies" />;
  }

  const rows = roster.data?.tenants ?? [];
  const exhausted = rows.filter((row) => row.usage?.exhausted).length;

  return (
    <>
      <PageHeader
        title="Companies"
        description="Every operator company, the tier it is on, and what it has committed against that tier's allowance — settled usage plus the reservations still in the air, which is the sum the gateway blocks on."
      />
      {exhausted > 0 ? (
        <Alert tone="warning" title={`${exhausted} of these has spent its allowance`}>
          <p>
            Their traffic is being refused at the reservation step — before a
            prompt leaves the building, not at settlement. Upgrading to a paid
            tier lifts the block on the next request, mid-period; nothing has to
            wait for a renewal.
          </p>
        </Alert>
      ) : null}
      <Card>
        <DataTable<TenantRosterRow>
          rows={rows}
          rowKey={(row) => row.id}
          empty="No company has signed up yet."
          columns={[
            {
              key: "name",
              header: "Company",
              render: (row) => (
                <>
                  <div>
                    <Link to="/tenants/$tenantId" params={{ tenantId: row.id }}>
                      {row.name}
                    </Link>
                  </div>
                  <code className="esm-muted-text">{row.slug}</code>
                </>
              ),
            },
            {
              key: "status",
              header: "Status",
              render: (row) => <StatusBadge status={row.status} />,
            },
            {
              key: "tier",
              header: "Tier",
              render: (row) =>
                row.tier ? (
                  <>
                    <div>{row.tier.name}</div>
                    <span className="esm-muted-text">
                      {row.tier.price.currency} {row.tier.price.amount}{" "}
                      {intervalLabel(row.tier)}
                    </span>
                  </>
                ) : (
                  <Badge tone="neutral">Not metered</Badge>
                ),
            },
            {
              key: "usage",
              header: "Committed / allowance",
              align: "end",
              render: (row) => {
                const use = consumption(row);
                if (!use) {
                  // No open period. Different from "used nothing": nothing is
                  // counting this company at all.
                  return <span className="esm-muted-text">—</span>;
                }
                return (
                  <>
                    <div>{use.label}</div>
                    <span className="esm-muted-text">{use.percent}%</span>
                  </>
                );
              },
            },
            {
              key: "in_flight",
              header: "In flight",
              align: "end",
              // Reported apart from the committed figure because the two are
              // different kinds of claim: one is what a provider billed, the
              // other is what has been promised and not yet reported.
              render: (row) =>
                row.usage ? (
                  <span>{tokenCount(row.usage.in_flight_tokens)}</span>
                ) : (
                  <span className="esm-muted-text">—</span>
                ),
            },
            {
              key: "period",
              header: "Period ends",
              render: (row) =>
                row.usage ? (
                  // A one-time grant is recorded as ending at the year-9999
                  // horizon rather than as a NULL — see tenancy.PerpetualPeriodEnd
                  // — so it is said in words instead of printed as a date
                  // nobody would believe.
                  row.usage.period_end.startsWith("9999") ? (
                    <span className="esm-muted-text">never expires</span>
                  ) : (
                    when(row.usage.period_end)
                  )
                ) : (
                  <span className="esm-muted-text">—</span>
                ),
            },
            {
              key: "exhausted",
              header: "",
              align: "end",
              render: (row) =>
                row.usage?.exhausted ? (
                  <Badge tone="danger">Blocked at reserve</Badge>
                ) : null,
            },
          ]}
        />
      </Card>
    </>
  );
}
